import React from "react";
import { useTranslation } from "react-i18next";
import { CreateBusinessForm } from "../../../../types/forms";
import { StepFlowStep } from "../../../components/misc/StepFlow";
import FeaturesForm from "../../../components/misc/FeaturesForm";
import { getMinimumBusinessSubscriptionForFeatures } from "../../../../utils/BusinessFeatureUtils";
import AreYouSureModal from "../../../components/modals/AreYouSureModal";

type BusinessFeaturesViewProps = {
  setStep: (step: 1 | -1) => void;
  business: CreateBusinessForm;
  setBusiness: (business: CreateBusinessForm) => void;
}

export default function BusinessFeaturesView({ business, setBusiness, setStep }: BusinessFeaturesViewProps) {
  const { t } = useTranslation();
  const [warningOpen, setWarningOpen] = React.useState(false);
  const [deferredStepChange, setDeferredStepChange] = React.useState<null | 1 | -1>(null);

  const wrappedSetStep = (diff: 1 | -1) => {
    const features = business.dashboardFeatures || {};
    const anySelected = Object.values(features).some(Boolean);

    if (!anySelected && diff === 1) {
      setDeferredStepChange(diff);
      setWarningOpen(true);
    } else {
      setStep(diff);
    }
  };

  return (
    <>
      <StepFlowStep
        disabled={business.dashboardFeatures === undefined}
        setStep={wrappedSetStep}
        title={t('page.onboarding.businessFeaturesScreen.title')}
        subtitle={t('page.onboarding.businessFeaturesScreen.subtitle')}
        className="max-w-4xl">
        <FeaturesForm
          isBusiness
          features={business.dashboardFeatures || {}}
          onFeaturesChange={(features) => {
            const requiredSubscription = getMinimumBusinessSubscriptionForFeatures(features);
            setBusiness({
              ...business,
              connectedMembers: features.EASY_SHARING,
              dashboardFeatures: features,
              subscriptionType: requiredSubscription,
            });
          }}
        />
      </StepFlowStep>

      <AreYouSureModal
        open={warningOpen}
        setOpen={setWarningOpen}
        title={t('page.onboarding.businessFeaturesScreen.modal.title')}
        description={t('page.onboarding.businessFeaturesScreen.modal.description')}
        confirmLabel={t('page.onboarding.businessFeaturesScreen.modal.confirm')}
        cancelLabel={t('page.onboarding.businessFeaturesScreen.modal.cancel')}
        onConfirm={() => {
          setWarningOpen(false);
          if (deferredStepChange) {
            setStep(deferredStepChange);
            setDeferredStepChange(null);
          }
        }}
        onCancel={() => {
          setWarningOpen(false);
          setDeferredStepChange(null);
        }}
      />
    </>
  );
}
